import type { EntityType } from "@/schemas/digital-files";
import { cn } from "@/utils/theme";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
} from "@elaniintc/goes-design-system";
import { useCallback, useEffect, useMemo, useState } from "react";
import { EntityStatusBadge } from "./EntityBadge";
import {
  getEntity,
  useGetEntitiesInfinite,
} from "@/queries/digital-files/entities.api";
import SearchInput from "@/components/common/search-input";
import { useDebounceHook } from "@/hooks/use-debounce";

interface EntitySelectorProps {
  selectedEntity: EntityType | null;
  selectedEntityId?: string;
  onEntityChange: (entity: EntityType) => void;
}

export const EntitySelector = ({
  selectedEntity,
  selectedEntityId,
  onEntityChange,
}: EntitySelectorProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounceHook(searchQuery, 300);
  const [notFoundEntity, setNotFoundEntity] = useState<EntityType | null>(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    dataUpdatedAt,
  } = useGetEntitiesInfinite({
    filter: {
      search: debouncedSearchQuery.trim() || "",
    },
  });

  const fetchEntityById = useCallback(
    async (selectedEntityId: string) => {
      try {
        const response = await getEntity(selectedEntityId);
        if (response?.data) {
          setNotFoundEntity(response.data);
          onEntityChange(response.data);
        }
      } catch (error) {
        console.error("Error fetching entity by ID:", error);
      }
    },
    [onEntityChange],
  );

  const entities = useMemo(() => {
    if (!data?.pages) return [];
    const allEntities = data.pages.flatMap((page) => page.data || []);

    // Filter to avoid duplicates
    const uniqueEntities = notFoundEntity
      ? allEntities.filter((entity) => entity.id !== notFoundEntity.id)
      : allEntities;

    const mergedEntities = notFoundEntity
      ? [notFoundEntity, ...uniqueEntities]
      : uniqueEntities;

    if (!debouncedSearchQuery.trim()) return mergedEntities;

    const query = debouncedSearchQuery.toLowerCase();
    return mergedEntities.filter((entity) =>
      entity.name.toLowerCase().includes(query),
    );
  }, [data, debouncedSearchQuery, notFoundEntity]);

  // Auto-select entity from URL or first entity on initial load
  useEffect(() => {
    if (entities.length === 0) return;
    if (!selectedEntityId) {
      // default to first entity
      onEntityChange(entities[0]);
    } else if (!selectedEntity) {
      const entityFromId = entities.find((e) => e.id === selectedEntityId);
      if (entityFromId) {
        onEntityChange(entityFromId);
      } else {
        fetchEntityById(selectedEntityId);
      }
    }
  }, [
    selectedEntityId,
    selectedEntity,
    dataUpdatedAt,
    entities,
    onEntityChange,
    fetchEntityById,
  ]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="tertiary" size="xs" className="font-normal">
          {selectedEntity && (
            <EntityStatusBadge
              entity={selectedEntity}
              shape="pill"
              textSufix="-quiet"
              className="text-neutral-muted size-6 flex justify-center items-center"
            >
              <Icon type="fas" name="folder-open" size="xs" />
            </EntityStatusBadge>
          )}

          {selectedEntity ? selectedEntity.name : "Seleccionar entidad"}
          {!selectedEntity && isLoading && (
            <Icon
              type="fas"
              name="spinner-third"
              size="sm"
              className="animate-spin text-xl text-current"
            />
          )}
          <Icon type="fas" name="chevron-down" size="sm" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="start"
        className={cn(
          "max-h-80 min-w-120 rounded-lg border-neutral-silent text-body-loud font-semibold bg-neutral-object p-0 flex flex-col",
        )}
      >
        <>
          <div className="sticky top-0 z-10 bg-neutral-object p-2 border-b border-neutral-quiet">
            <SearchInput
              autoFocus
              className="w-full text-size-body font-normal"
              variant="md"
              placeholder="Buscar entidad..."
              value={searchQuery}
              onChange={(e) => {
                e.stopPropagation();
                setSearchQuery(e.target.value);
              }}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            />
          </div>
          {isLoading ? (
            <div className="flex justify-center p-4">
              <Icon
                type="fas"
                name="spinner-third"
                className="animate-spin text-on-disabled"
              />
            </div>
          ) : entities.length === 0 ? (
            <div className="p-4 text-center text-neutral-muted h-40 flex items-center justify-center">
              <span>No se encontraron entidades</span>
            </div>
          ) : (
            <div className="overflow-y-auto flex-1">
              {entities.map((element) => (
                <DropdownMenuItem
                  key={element.id}
                  onSelect={() => onEntityChange(element)}
                  className="flex justify-between items-center gap-2"
                >
                  {element.name}{" "}
                  <EntityStatusBadge entity={element}>
                    {`v${element.version} ${element?.status_label ?? element?.status}`}
                  </EntityStatusBadge>
                </DropdownMenuItem>
              ))}
              {hasNextPage && (
                <div className="w-full flex items-center justify-start border-t border-neutral-quiet mt-1 p-2">
                  <Button
                    variant="ghost-blue"
                    size="xs"
                    type="button"
                    disabled={isFetchingNextPage}
                    className="shadow-none"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!isFetchingNextPage) {
                        fetchNextPage();
                      }
                    }}
                  >
                    Más resultados
                    <Icon
                      type="far"
                      name={isFetchingNextPage ? "spinner-third" : "plus"}
                      className={cn(
                        "text-current",
                        isFetchingNextPage && "animate-spin",
                      )}
                    />
                  </Button>
                </div>
              )}
            </div>
          )}
        </>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
